import { getUserData } from "@/auth/getUserData";
import useChats from "@/composables/useChats";
import useChannels from "@/composables/v2.1/useChannels";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useMultiSelect from "@/composables/v2.1/useMultiSelect";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import ChannelInterface from "@/interfaces/channel.interface";
import { ChannelSettingInterface } from "@/interfaces/channelSettings.interface";
import MultiSelectChatInterface from "@/interfaces/multiSelectChat.interface";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";
import { UserRoleType } from "@/types/userRole.type";
import { ReadDto } from "@saysimple-platform/dtos";
import { isArray } from "lodash";

interface OptionsInterface {
    showToast: boolean;
    isNewActiveChatEntry?: boolean;
    readForAllUsers?: number;
}

export default function useReadChat(toast: any) {
    const { publicApiEndpointPath } = useInboxModuleType();
    const { channels } = useChannels();
    const { addUnreadChat, deleteUnreadChat } = useUnreadChats();

    async function setRead(
        chat: MultiSelectChatInterface | MultiSelectChatInterface[],
        options: OptionsInterface = { showToast: true, isNewActiveChatEntry: false }
    ): Promise<void> {
        /*
        - When set read: set chat from chats list to read and set unread messages to zero.
        - When set unread: set chat from chats list to unread.
         */
        const chats = isArray(chat) ? chat : [chat];
        if (!chats[0]) {
            return;
        }

        const { updateChatInChatsList } = useChats();
        const { role } = getUserData();

        if (role === UserRoleType.SAYSIMPLE) {
            options.readForAllUsers = 0;
        }

        const readResults: {
            ok: MultiSelectChatInterface[];
            error: MultiSelectChatInterface[];
        } = {
            ok: [],
            error: [],
        };

        await Promise.all(
            chats.map(async (chat) => {
                if (!chat.id) {
                    throw new Error("No id passed.");
                }

                deleteUnreadChat(chat.id);

                await updateChatInChatsList(chat.id, {
                    read: true,
                    unreadMessages: 0,
                }).then(async (response) => {
                    if (response?.updatedData?.read || options.isNewActiveChatEntry) {
                        let readForAll = 0;

                        if (role !== UserRoleType.SAYSIMPLE && response?.updatedData?.channelId) {
                            const channelSettings: ChannelSettingInterface[] =
                                channels.value.find(
                                    (channel: ChannelInterface) => channel.id === response.updatedData?.channelId
                                )?.settings ?? [];

                            readForAll = Number(
                                channelSettings.find((channelSetting) => channelSetting.name === "readForAllUsers")
                                    ?.value ?? 0
                            );
                        }

                        await axios
                            .post(
                                `/${publicApiEndpointPath.value}/read`,
                                {
                                    chatId: chat.id,
                                },
                                {
                                    params: {
                                        "read-for-all-users": readForAll,
                                    },
                                }
                            )
                            .then(() => readResults.ok.push(chat))
                            .catch(() => readResults.error.push(chat));
                    }
                });
            })
        );

        const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();
        if (multiSelectEnabled.value) {
            clearMultiSelect();
        }

        if (options?.showToast) {
            let status = "success";
            let notificationText = i18n.t("entities.conversation.set-read-amount", {
                amount: readResults.ok.length,
                entity: i18n.tc("entities.conversation.conversations", readResults.ok.length),
            });

            if (readResults.ok.length > 0 && readResults.error.length > 0) {
                status = "warning";
                notificationText = i18n.t("entities.conversation.set-read-multiple-amount", {
                    amountOk: readResults.ok.length,
                    entityOk: i18n.tc("entities.conversation.conversations", readResults.ok.length),
                    amountError: readResults.error.length,
                    entityError: i18n.tc("entities.conversation.conversations", readResults.error.length),
                });
            } else if (readResults.ok.length === 0 && readResults.error.length > 0) {
                status = "error";
                notificationText = i18n.t("entities.conversation.could-not-set-read-amount", {
                    amount: readResults.error.length,
                    entity: i18n.tc("entities.conversation.conversations", readResults.error.length),
                });
            }

            toast({
                component: toastificationContent,
                props: {
                    title: i18n.t("entities.conversation.mark-as-read"),
                    text: notificationText,
                    icon: status === "error" ? "times" : "check",
                    variant: status,
                },
            });
        }
    }

    async function setUnread(
        chat: MultiSelectChatInterface | MultiSelectChatInterface[],
        options: OptionsInterface = { showToast: true }
    ) {
        const chats = isArray(chat) ? chat : [chat];
        if (!chats[0]) {
            return;
        }

        const { updateChatInChatsList } = useChats();

        const unreadResults: {
            ok: MultiSelectChatInterface[];
            error: MultiSelectChatInterface[];
        } = {
            ok: [],
            error: [],
        };

        await Promise.all(
            chats.map(async (chat) => {
                if (!chat.id) {
                    throw new Error("No id passed.");
                }

                // @TODO: fix as unknown
                addUnreadChat({
                    chatId: chat.id,
                    channelId: Number(chat.channel?.id),
                    tagIds: chat?.tags.map((tag) => tag.id) ?? [],
                    assignedUserId: chat.assignedUserId,
                    assignedTeamId: chat.assignedTeamId,
                    userId: chat.userId,
                    status: "open",
                    readForAll: chat.readForAll,
                    isChatRead: chat.isChatRead,
                    lastEntryReadAt: chat.lastEntryReadAt,
                } as unknown as ReadDto);

                await updateChatInChatsList(chat.id, { read: false }).then(async (response) => {
                    // @ts-ignore
                    if (response && response.updatedData?.read === false) {
                        await axios
                            .post(`/${publicApiEndpointPath.value}/unread`, {
                                chatId: chat.id,
                            })
                            .then(() => unreadResults.ok.push(chat))
                            .catch(() => unreadResults.error.push(chat));
                    }
                });
            })
        );

        const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();
        if (multiSelectEnabled.value) {
            clearMultiSelect();
        }

        if (options?.showToast) {
            let status = "success";
            let notificationText = i18n.t("entities.conversation.set-read-amount", {
                amount: unreadResults.ok.length,
                entity: i18n.tc("entities.conversation.conversations", unreadResults.ok.length),
            });

            if (unreadResults.ok.length > 0 && unreadResults.error.length > 0) {
                status = "warning";
                notificationText = i18n.t("entities.conversation.set-read-multiple-amount", {
                    amountOk: unreadResults.ok.length,
                    entityOk: i18n.tc("entities.conversation.conversations", unreadResults.ok.length),
                    amountError: unreadResults.error.length,
                    entityError: i18n.tc("entities.conversation.conversations", unreadResults.error.length),
                });
            } else if (unreadResults.ok.length === 0 && unreadResults.error.length > 0) {
                status = "error";
                notificationText = i18n.t("entities.conversation.could-not-set-read-amount", {
                    amount: unreadResults.error.length,
                    entity: i18n.tc("entities.conversation.conversations", unreadResults.error.length),
                });
            }

            toast({
                component: toastificationContent,
                props: {
                    title: i18n.t("entities.conversation.mark-as-unread"),
                    text: notificationText,
                    icon: status === "error" ? "times" : "check",
                    variant: status,
                },
            });
        }
    }

    return {
        setRead,
        setUnread,
    };
}
